/*!
 * Doka Image Editor 7.6.0
 * (c) 2018-2021 PQINA Inc. - All Rights Reserved
 * License: https://pqina.nl/doka/license/
 *
 * ****This files was modified to fit our needs****
 * Optional 7th argument `beforeOpenEditor`: async (file) => boolean. When it returns false,
 * the editor is not opened and the queue advances (used for minimum image size before Doka).
 * Optional 8th `resolveImageCropMinSize`: () => { width, height } for Pintura `imageCropMinSize`.
 * Optional 9th `beforeAddProcessedFile`: async (dest) => boolean. When false, processed output is not added.
 */

function useEditorWithUppy(
  openEditor,
  getOptions,
  uppy,
  getCurrentFilesCount,
  maxNumberOfFiles,
  resolveImageCropAspectRatio,
  beforeOpenEditor,
  resolveImageCropMinSize,
  beforeAddProcessedFile,
) {
  const queue = []

  const editNextFile = () => {
    const next = queue[0]
    if (!next) {
      return
    }

    Promise.resolve(next()).catch(() => {
      queue.shift()
      editNextFile()
    })
  }

  const queueFile = (file) => {
    queue.push(async function runEditor() {
      if (typeof beforeOpenEditor === 'function') {
        const ok = await beforeOpenEditor(file)
        if (!ok) {
          queue.shift()
          editNextFile()
          return
        }
      }

      if (getCurrentFilesCount() >= maxNumberOfFiles) {
        queue.length = 0
        uppy.emit('error', Error('max-files-reached'))
        return
      }

      const options = getOptions()
      let imageCropAspectRatio = options.imageCropAspectRatio
      if (typeof resolveImageCropAspectRatio === 'function') {
        imageCropAspectRatio = await resolveImageCropAspectRatio(file)
      }

      const editorOptions = {
        ...options,
        imageCropAspectRatio,
        src: file.data,
      }

      if (typeof resolveImageCropMinSize === 'function') {
        editorOptions.imageCropMinSize = resolveImageCropMinSize()
      }

      const editor = openEditor(editorOptions)

      editor.on('hide', () => {
        // Remove this item from the queue
        queue.shift()

        // Edit next item in queue
        editNextFile()
      })

      editor.on('process', async ({ dest }) => {
        // Don't add file if cancelled
        if (!dest) {
          return
        }

        if (typeof beforeAddProcessedFile === 'function') {
          const ok = await beforeAddProcessedFile(dest)
          if (!ok) {
            return
          }
        }

        uppy.addFile({
          // clone file
          ...file,

          // overwrite file data
          data: dest,

          // update metadata so we know this file was edited
          meta: {
            ...file.meta,
            didEdit: true,
          },
        })
      })
    })

    // If this is first item, let's open the editor immmidiately
    if (queue.length === 1) {
      editNextFile()
    }
  }

  return (file) => {
    if (file?.meta?.replaceFileId || file?.meta?.didEdit) {
      return true
    }

    // edit first, then add manually
    queueFile(file)

    // can't add now, we have to wait for editing to finish
    return false
  }
}

export default useEditorWithUppy
